<template>
  <div class="date-picker-container">
    <div class="grid grid-cols-3 gap-3">
      <!-- Day Selector -->
      <div class="form-group">
        <label class="block text-xs text-gray-500 mb-1">Day</label>
        <div class="scroll-select-container">
          <select
            v-model="selectedDay"
            @change="updateDate"
            class="scroll-select form-control text-sm"
          >
            <option value="">Day</option>
            <option v-for="day in daysInMonth" :key="day" :value="day">
              {{ String(day).padStart(2, '0') }}
            </option>
          </select>
        </div>
      </div>

      <!-- Month Selector -->
      <div class="form-group">
        <label class="block text-xs text-gray-500 mb-1">Month</label>
        <div class="scroll-select-container">
          <select
            v-model="selectedMonth"
            @change="updateDate"
            class="scroll-select form-control text-sm"
          >
            <option value="">Month</option>
            <option v-for="(month, index) in months" :key="index + 1" :value="index + 1">
              {{ month }}
            </option>
          </select>
        </div>
      </div>

      <!-- Year Selector -->
      <div class="form-group">
        <label class="block text-xs text-gray-500 mb-1">Year</label>
        <div class="scroll-select-container">
          <select
            v-model="selectedYear"
            @change="updateDate"
            class="scroll-select form-control text-sm"
          >
            <option value="">Year</option>
            <option v-for="year in yearOptions" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>
      </div>
    </div>
    
    <!-- Age validation message -->
    <div v-if="ageError" class="mt-2 text-sm text-red-600 flex items-center">
      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      {{ ageError }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';

interface Props {
  modelValue?: string;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const selectedDay = ref<number | ''>('');
const selectedMonth = ref<number | ''>('');
const selectedYear = ref<number | ''>('');
const ageError = ref<string>('');

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Generate year options (18+ years old restriction)
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - 18; // Must be at least 18 years old
  const minYear = maxYear - 80; // Reasonable range
  const years = [];
  
  for (let year = maxYear; year >= minYear; year--) {
    years.push(year);
  }
  
  return years;
});

// Calculate days in selected month
const daysInMonth = computed(() => {
  if (!selectedMonth.value || !selectedYear.value) {
    return Array.from({ length: 31 }, (_, i) => i + 1);
  }
  
  const daysCount = new Date(selectedYear.value as number, selectedMonth.value as number, 0).getDate();
  return Array.from({ length: daysCount }, (_, i) => i + 1);
});

// Validate age (must be 18+)
const validateAge = () => {
  if (selectedDay.value && selectedMonth.value && selectedYear.value) {
    const birthDate = new Date(selectedYear.value as number, (selectedMonth.value as number) - 1, selectedDay.value as number);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      const actualAge = age - 1;
      if (actualAge < 18) {
        ageError.value = `You must be at least 18 years old. Current age: ${actualAge}`;
        return false;
      }
    } else if (age < 18) {
      ageError.value = `You must be at least 18 years old. Current age: ${age}`;
      return false;
    }
  }
  
  ageError.value = '';
  return true;
};

const updateDate = () => {
  // Validate day exists in selected month
  if (selectedDay.value && selectedMonth.value && selectedYear.value) {
    const maxDays = new Date(selectedYear.value as number, selectedMonth.value as number, 0).getDate();
    if ((selectedDay.value as number) > maxDays) {
      selectedDay.value = maxDays;
    }
  }
  
  if (selectedDay.value && selectedMonth.value && selectedYear.value) {
    if (validateAge()) {
      const dateString = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}-${String(selectedDay.value).padStart(2, '0')}`;
      emit('update:modelValue', dateString);
    } else {
      emit('update:modelValue', '');
    }
  } else {
    emit('update:modelValue', '');
  }
};

// Parse initial value
const parseInitialValue = () => {
  if (props.modelValue) {
    const date = new Date(props.modelValue);
    if (!isNaN(date.getTime())) {
      selectedYear.value = date.getFullYear();
      selectedMonth.value = date.getMonth() + 1;
      selectedDay.value = date.getDate();
      validateAge();
    }
  }
};

// Watch for external changes
watch(() => props.modelValue, parseInitialValue);

onMounted(() => {
  parseInitialValue();
});
</script>

<style scoped>
@reference "../styles.css";

.date-picker-container {
  @apply w-full;
}

.scroll-select-container {
  @apply relative;
}

.scroll-select {
  @apply appearance-none cursor-pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.scroll-select:focus {
  @apply ring-2 ring-blue-500 border-transparent;
}

/* Custom scrollbar for better UX */
.scroll-select option {
  @apply py-2 px-3;
}
</style>
